
import { useEffect, useMemo, useState } from "react";
import "./EditActiveRule.css";
import searchruleIcon from "../../../common/assets/images/searchrule.png";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MultiSelect from "../../../utils/MultiSelect";
import {
  fetchPathwayOptions,
  fetchTriggerCodeOptions,
  submitRuleManagement,
  RULE_MANAGEMENT_ACTIONS,
  fetchEnvironmentOptions
} from "../CreateNewRule/redux/CreateNewRuleSlicer";
import { getRuleDetails } from "../ViewRuleDatabase/Redux/ruleDatabaseSlice";
import { useParams } from "react-router-dom";
import { getRuleIDStatusRepsone } from "../TestRule/TestRuleRedux";
import PublishStatusModal from "../PublishedRule/PublishStatusModal";

// Same pathway index types Create New Rule offers - needed because the
// pathway search API is keyed by code_type (icd/achi).
const PATHWAY_OPTIONS = [
  { value: "icd", label: "ICD Index" },
  { value: "achi", label: "ACHI Index" },
];
const CODE_TYPE_LABELS = {
  icd: "ICD-10-AM",
  achi: "ACHI",
};
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_PAGE_SIZE = 10;
const SCROLL_LOAD_MORE_THRESHOLD_PX = 24;

function pathwayValueFromLabel(label) {
  const opt = PATHWAY_OPTIONS.find((o) => o.label === label);
  return opt ? opt.value : "";
}

export default function EditRule({ onCancel, onSaveAndTest, onSave,isArchived = false  }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Which rule we're editing comes from wherever "Edit" was clicked
  // (e.g. the Rule Database table sets this before navigating here).
  // const codeAssistManualRuleIdFromStore = useSelector(
  //   (state) => state.ruleDatabase.selectedEditRuleId
  // );
   const activeRuleId = useSelector(
    (state) => state.ruleDatabase.selectedEditRuleId
  );
  const archivedRuleId = useSelector(
    (state) => state.archiveRule.selectedRuleId
  );
  const codeAssistManualRuleIdFromStore = isArchived ? archivedRuleId : activeRuleId;
  // Archived rule edit uses archiveRule slice's id (already set by ArchivedRule.jsx)

  const [ruleId, setRuleId] = useState("");
  const [status, setStatus] = useState("");

  const [description, setDescription] = useState("");
  const [environments, setEnvironments] = useState([]);

  const [pathwayType, setPathwayType] = useState("");
  const [entryPointQuery, setEntryPointQuery] = useState("");
  const [entryPoint, setEntryPoint] = useState("");
  const [entryPointOpen, setEntryPointOpen] = useState(false);
  const [pathwayPage, setPathwayPage] = useState(1);

  const [codeQuery, setCodeQuery] = useState("");
  const [codeOpen, setCodeOpen] = useState(false);
  const [codePage, setCodePage] = useState(1);
  const [codes, setCodes] = useState([]);

  const [configuredGuidance, setConfiguredGuidance] = useState("");
  const [recommendedAction, setRecommendedAction] = useState("");

  // Baseline taken from the API response (not hardcoded defaults anymore).
  // Save/Save-and-Test stay disabled until this is set and something changes.
  const [initialValues, setInitialValues] = useState(null);

  const [isLoadingRule, setIsLoadingRule] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishModalType, setPublishModalType] = useState("loading");
  const [ruleStatusResponse, setRuleStatusResponse] = useState(null);

  // Same slice state Create New Rule reads its pathway/trigger-code search
  // results from - both pages share one API and one slice.
  const pathwayOptionsRaw = useSelector((state) => state.createNewRule?.pathwayOptions);
  const pathwayOptions = Array.isArray(pathwayOptionsRaw) ? pathwayOptionsRaw : [];
  const pathwayLoading = useSelector((state) => state.createNewRule?.pathwayLoading);
  const pathwayHasMore = useSelector((state) => state.createNewRule?.pathwayHasMore);

  const triggerCodeOptionsRaw = useSelector((state) => state.createNewRule?.triggerCodeOptions);
  const triggerCodeOptions = Array.isArray(triggerCodeOptionsRaw) ? triggerCodeOptionsRaw : [];
  const triggerCodeLoading = useSelector((state) => state.createNewRule?.triggerCodeLoading);
  const triggerCodeHasMore = useSelector((state) => state.createNewRule?.triggerCodeHasMore);
  // 2. Inside the component, replace the environment-related selectors/state:

  // Environment options now come from the API (same slice CreateNewRule uses)
  const environmentOptionsRaw = useSelector((state) => state.createNewRule?.environmentOptions);
  const environmentOptionsList = Array.isArray(environmentOptionsRaw) ? environmentOptionsRaw : [];
  const environmentLoading = useSelector((state) => state.createNewRule?.environmentLoading);

  // Map flat string list -> { value, label } shape MultiSelect expects
  // const ENVIRONMENT_OPTIONS = useMemo(
  //   () => environmentOptionsList.map((env) => ({ value: env, label: env })),
  //   [environmentOptionsList]
  // );
  // AFTER
const ENVIRONMENT_OPTIONS = useMemo(() => {
  const base = environmentOptionsList.map((env) => ({ value: env, label: env }));
  const knownValues = new Set(base.map((o) => o.value));
  const orphaned = environments
    .filter((val) => !knownValues.has(val))
    .map((val) => ({ value: val, label: val }));

  return [...base, ...orphaned];
}, [environmentOptionsList, environments]);
  const selectedEnvOptions = ENVIRONMENT_OPTIONS.filter((opt) =>
    environments.includes(opt.value)
  );
  // ---- Fetch the rule to edit as soon as we know its id ----
 useEffect(() => {
  if (!codeAssistManualRuleIdFromStore) {
    const message = "No rule was selected to edit.";
    setLoadError(message);
    toast.error(message);
    return;
  }

  let isCancelled = false;

  async function loadRule() {
    setIsLoadingRule(true);
    setLoadError(null);

    // get_rule_details only needs the id — it's a read, not a management action
    const payload = {
      code_assist_manual_rule_id: codeAssistManualRuleIdFromStore,
    };

    try {
      const data = await dispatch(getRuleDetails(payload)).unwrap();
      if (isCancelled) return;
      applyRuleResponse(data);
    } catch (error) {
      if (isCancelled) return;
      const message = error || "Could not load this rule.";
      setLoadError(message);
      toast.error(message);
    } finally {
      if (!isCancelled) setIsLoadingRule(false);
    }
  }

  loadRule();
  return () => {
    isCancelled = true;
  };
}, [dispatch, codeAssistManualRuleIdFromStore]);
  // 3. Fetch environment options on mount if not already loaded
  useEffect(() => {
    if (!environmentOptionsList.length) {
      dispatch(fetchEnvironmentOptions());
    }
  }, [dispatch, environmentOptionsList.length]);
  
  function applyRuleResponse(data) {
    const nextDescription = data?.description ?? "";
    const nextEnvironments = Array.isArray(data?.environment) ? data.environment : [];
    const nextPathwayType = pathwayValueFromLabel(data?.pathway_entry?.type);
    const nextEntryPoint = data?.pathway_entry?.value ?? "";
    const nextCodes = Array.isArray(data?.trigger_codes)
      ? data.trigger_codes.map((c) => ({
        code: c.code,
        label: c.code_description || "",
        codeType: c.code_type || null,
      }))
      : [];
    const nextConfiguredGuidance = data?.guidance?.configured_guidance ?? "";
    const nextRecommendedAction = data?.guidance?.recommended_action ?? "";

    setRuleId(data?.rule_id ?? codeAssistManualRuleIdFromStore ?? "");
    setStatus(data?.status ?? "Active");
    setDescription(nextDescription);
    setEnvironments(nextEnvironments);
    setPathwayType(nextPathwayType);
    setEntryPoint(nextEntryPoint);
    setCodes(nextCodes);
    setConfiguredGuidance(nextConfiguredGuidance);
    setRecommendedAction(nextRecommendedAction);

    setInitialValues({
      description: nextDescription,
      environments: nextEnvironments,
      pathwayType: nextPathwayType,
      entryPoint: nextEntryPoint,
      codes: nextCodes,
      configuredGuidance: nextConfiguredGuidance,
      recommendedAction: nextRecommendedAction,
    });
  }

  function arraysEqualUnordered(a, b) {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((v, i) => v === sortedB[i]);
  }

  function codesEqualUnordered(a, b) {
    return arraysEqualUnordered(a.map((c) => c.code), b.map((c) => c.code));
  }

  const isEnvironmentChanged = initialValues
    ? !arraysEqualUnordered(environments, initialValues.environments)
    : false;

  const isOtherFieldChanged = initialValues
    ? description !== initialValues.description ||
    pathwayType !== initialValues.pathwayType ||
    entryPoint !== initialValues.entryPoint ||
    !codesEqualUnordered(codes, initialValues.codes) ||
    configuredGuidance !== initialValues.configuredGuidance ||
    recommendedAction !== initialValues.recommendedAction
    : false;

  const hasAnyChange = isEnvironmentChanged || isOtherFieldChanged;
  const isSaveOnlyMode = isEnvironmentChanged && !isOtherFieldChanged;
  const submitActionType = isSaveOnlyMode
    ? RULE_MANAGEMENT_ACTIONS.SAVE
    : RULE_MANAGEMENT_ACTIONS.SAVE_AND_TEST;
  const submitLabel = isSaveOnlyMode ? "Save" : "Save and Test";
  const isSubmitEnabled = !!initialValues && hasAnyChange;
  // Debounced Clinical Pathway search (page 1) - fires whenever the dropdown
  // is open and an index type is selected. Same as Create New Rule.
  useEffect(() => {
    if (pathwayType === "" || !entryPointOpen) return undefined;

    const handle = setTimeout(() => {
      setPathwayPage(1);
      dispatch(
        fetchPathwayOptions({
          search_code: entryPointQuery.trim(),
          search_description: null,
          code_type: pathwayType,
          page_number: 1,
          page_size: SEARCH_PAGE_SIZE,
        })
      );
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [dispatch, pathwayType, entryPointQuery, entryPointOpen]);

  // Debounced Trigger Codes search (page 1). Same as Create New Rule.
  useEffect(() => {
    if (!codeOpen) return undefined;

    const handle = setTimeout(() => {
      setCodePage(1);
      dispatch(
        fetchTriggerCodeOptions({
          search_code: codeQuery.trim(),
          search_description: null,
          page_number: 1,
          page_size: SEARCH_PAGE_SIZE,
        })
      );
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [dispatch, codeQuery, codeOpen]);

  const filteredCodeSuggestions = useMemo(() => {
    const already = new Set(codes.map((c) => c.code));
    return triggerCodeOptions.filter((c) => !already.has(c.code));
  }, [triggerCodeOptions, codes]);

  function handlePathwayTypeChange(e) {
    setPathwayType(e.target.value);
    setEntryPoint("");
    setEntryPointQuery("");
  }

  function handleSelectPathwayOption(option) {
    setEntryPoint(option.code);
    setEntryPointQuery("");
    setEntryPointOpen(false);
  }

  // Infinite scroll: loads the next page of pathway suggestions.
  function handlePathwaySuggestionsScroll(e) {
    if (!pathwayHasMore || pathwayLoading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight > SCROLL_LOAD_MORE_THRESHOLD_PX) return;

    const nextPage = pathwayPage + 1;
    setPathwayPage(nextPage);
    dispatch(
      fetchPathwayOptions({
        search_code: entryPointQuery.trim(),
        search_description: "",
        code_type: pathwayType,
        page_number: nextPage,
        page_size: SEARCH_PAGE_SIZE,
      })
    );
  }

  // Infinite scroll: loads the next page of trigger code suggestions.
  function handleTriggerCodeSuggestionsScroll(e) {
    if (!triggerCodeHasMore || triggerCodeLoading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight > SCROLL_LOAD_MORE_THRESHOLD_PX) return;

    const nextPage = codePage + 1;
    setCodePage(nextPage);
    dispatch(
      fetchTriggerCodeOptions({
        search_code: codeQuery.trim() || null,
        search_description: null,
        page_number: nextPage,
        page_size: SEARCH_PAGE_SIZE,
      })
    );
  }

  function addCode(option) {
    setCodes((prev) => {
      if (prev.some((c) => c.code === option.code)) return prev;
      return [
        ...prev,
        {
          code: option.code,
          label: option.description || "",
          codeType: option.code_type || null,
        },
      ];
    });
    setCodeQuery("");
    setCodeOpen(false);
  }

  function removeCode(code) {
    setCodes((prev) => prev.filter((c) => c.code !== code));
  }

  // Same payload shape Create New Rule sends - just also carries the id
  // so the backend knows this is an update to an existing rule.
  function buildPayload(actionType) {
    const pathwayLabel =
      pathwayType !== ""
        ? PATHWAY_OPTIONS.find((opt) => opt.value === pathwayType)?.label || null
        : null;

    return {
      code_assist_manual_rule_id: codeAssistManualRuleIdFromStore,
      rule_id: ruleId || null,
      description: description.trim() || null,
      environment: environments,
      pathway_entry:
        pathwayType !== "" 
          ? { type: pathwayLabel, clinical_pathway: entryPoint || null }
          : null,
      trigger_codes:
        codes.length > 0
          ? codes.map((c) => ({ code: c.code, 
             code_type: CODE_TYPE_LABELS[c.codeType?.toLowerCase()] || c.codeType || null,
          }))
          : null,
      guidance:
        configuredGuidance.trim() || recommendedAction.trim()
          ? {
            configured_guidance: configuredGuidance.trim() || null,
            recommended_action: recommendedAction.trim() || null,
          }
          : null,
      action: actionType,
    };
  }

  async function callApi(actionType) {
    setSubmitError(null);
    setPendingAction(actionType);
    try {
      const data = await dispatch(submitRuleManagement(buildPayload(actionType))).unwrap();
      return { ok: true, data };
    } catch (error) {
      return { ok: false, error };
    } finally {
      setPendingAction(null);
    }
  }
  const wait = (ms) =>
  new Promise((resolve) => setTimeout(resolve, ms));

  const pollRuleStatus = async (jobId) => {
  if (!jobId) {
    setRuleStatusResponse({
      items: [
        {
          status: "FAILED",
          error_message: "Job ID was not returned.",
        },
      ],
    });
    setPublishModalType("result");
    setShowPublishModal(true);
    return;
  }

  while (true) {
    try {
      const result = await dispatch(
        getRuleIDStatusRepsone(jobId)
      ).unwrap();

      console.log(result, "status API response");

      const items = result?.items || [];

      const isCompleted =
        items.length > 0 &&
        items.every(
          (item) =>
            item?.status === "COMPLETED" ||
            item?.status === "FAILED"
        );

      if (isCompleted) {
        setRuleStatusResponse(result);
        setPublishModalType("result");
        setShowPublishModal(true);
        return;
      }

      await wait(5000);
    } catch (error) {
      console.error("Status API failed:", error);

      setRuleStatusResponse({
        items: [
          {
            status: "FAILED",
            error_message: "Failed to fetch rule status.",
          },
        ],
      });

      setPublishModalType("result");
      setShowPublishModal(true);
      return;
    }
  }
};

  async function handleSubmit() {
    const result = await callApi(submitActionType);
    if (!result.ok) {
    const message =
    result.error || "Something went wrong while saving the rule.";
      setSubmitError(message);
      toast.error(message);
    return;
  }

  const jobId = result.data?.job_id;

  if (!jobId) {
    toast.error(
      "The request was successful, but the job ID was not returned."
    );
    return;
  }

  setRuleStatusResponse(null);

  setPublishModalType("loading");
  setShowPublishModal(true);
  pollRuleStatus(jobId);
  if (submitActionType === RULE_MANAGEMENT_ACTIONS.SAVE) {
    // applyRuleResponse(result.data);
    if (onSave) {
      onSave(result.data);
    }
    }
  }
  const handleCancelEdit=()=>{
    if (isArchived) {
      navigate("/archived-rule");
    } else {
      navigate("/view-rule-database");
    }
  }
  return (
    <>
    <div className="editRule-page">
      <ToastContainer autoClose={3000}/>
      {/* Page header */}
      <div className="editRule-page-header">
        <span className="editRule-page-header-bar" aria-hidden="true" />
        <h1 className="editRule-page-title">Edit Rule</h1>
      </div>

      <div className="editRule-card">
        {/* Status bar */}
        <div className="editRule-status-bar">
          <div className="editRule-status-left">
            <span className="editRule-id">{ruleId || "—"}</span>
            {status && (
              <span
                className={`editRule-status-badge ${status.toLowerCase() === "active"
                    ? "editRule-status-badge--active"
                    : "editRule-status-badge--neutral"
                  }`}
              >
                {status.toLowerCase() === "active" && (
                  <span className="editRule-status-badge-icon" aria-hidden="true">
                    &#10003;
                  </span>
                )}
                {status}
              </span>
            )}
          </div>
          <span className="editRule-status-meta">
            {isLoadingRule
              ? "Loading rule..."
              : `Editing Rule: ${ruleId || "—"} | Current Status: ${status || "—"}`}
          </span>
        </div>
        <div className="editRule-card-body">
          {/* Rule Information */}
          <section className="editRule-section">
            <h2 className="editRule-section-title">Rule Information</h2>

            <div className="editRule-field">
              <label className="editRule-label" htmlFor="edit-rule-description">
                Rule Description
                <span className="editRule-label-required" aria-hidden="true">
                  *
                </span>
              </label>
              <textarea
                id="edit-rule-description"
                className="editRule-textarea"
                rows={2}
                value={description}
                disabled={isLoadingRule}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            {status?.toLowerCase() !== "draft" && (
            <div className="editRule-field">
              <label className="editRule-label" htmlFor="publishing-environment">
                Publishing Environment
                <span className="editRule-label-required" aria-hidden="true">
                  *
                </span>
              </label>
              <div className="editRule-envRow">
                <div className="editRule-envMultiSelect">
                  <MultiSelect
                    id="publishing-environment"
                    options={ENVIRONMENT_OPTIONS}
                    isMulti={true}
                    value={selectedEnvOptions}
                    isDisabled={isLoadingRule}
                     isLoading={environmentLoading}
                    onChange={(selected) => {
                      setEnvironments(selected ? selected.map((opt) => opt.value) : []);
                    }}
                    placeholder="Select environment"
                  />
                </div>

                <div className="editRule-envChips">
                  {environments.length === 0 ? (
                    <span className="editRule-envChips-empty">No environment selected</span>
                  ) : (
                    environments.map((val) => {
                      const opt = ENVIRONMENT_OPTIONS.find((o) => o.value === val);
                      return (
                        <span className="editRule-envChip" key={val}>
                          {opt ? opt.label : val}
                          <button
                            type="button"
                            className="editRule-envChip-remove"
                            aria-label={`Remove ${opt ? opt.label : val}`}
                            onClick={() =>
                              setEnvironments((prev) => prev.filter((v) => v !== val))
                            }
                          >
                            &#215;
                          </button>
                        </span>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
            )}
          </section>

          {/* Pathway Entry Point */}
          <section className="editRule-section">
            <div className="editRule-section-heading-row">
              <h2 className="editRule-section-title">Pathway Entry Point</h2>
              <select
                className="editRule-pathway-select"
                value={pathwayType}
                disabled={isLoadingRule}
                onChange={handlePathwayTypeChange}
                aria-label="Pathway Entry Point type"
              >
                <option value="" disabled hidden>
                  Select Pathway Entry Point
                </option>
                {PATHWAY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="editRule-field">
              <label className="editRule-label" htmlFor="primary-entry-point">
                Primary Entry Point
                <span className="editRule-label-required" aria-hidden="true">
                  *
                </span>
              </label>
              <div className="editRule-combobox">
                <span className="editRule-combobox-icon" aria-hidden="true">
                  <img src={searchruleIcon} alt="search" />
                </span>
                <input
                  id="primary-entry-point"
                  type="text"
                  autoComplete="off"
                  className="editRule-input editRule-input--search"
                  placeholder={
                    pathwayType === ""
                      ? "Select a Pathway Entry Point type first..."
                      : "Select an index to search..."
                  }
                  value={entryPoint || entryPointQuery}
                  disabled={isLoadingRule || pathwayType === " "}
                  onChange={(e) => {
                    setEntryPoint("");
                    setEntryPointQuery(e.target.value);
                    setEntryPointOpen(true);
                  }}
                  onFocus={() => setEntryPointOpen(true)}
                  onBlur={() => setTimeout(() => setEntryPointOpen(false), 120)}
                />
                <span className="editRule-select-chevron" aria-hidden="true">
                  &#9662;
                </span>
                {entryPointOpen && pathwayType !== "" && (
                  <div
                    className="editRule-suggestions"
                    onScroll={handlePathwaySuggestionsScroll}
                  >
                    {pathwayOptions.length === 0 && pathwayLoading ? (
                      <div className="editRule-suggestions-empty editRule-suggestions-loading">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Searching...
                      </div>
                    ) : pathwayOptions.length === 0 ? (
                      <div className="editRule-suggestions-empty">
                        No matching entries.
                      </div>
                    ) : (
                      <>
                        {pathwayOptions.map((option) => (
                          <button
                            type="button"
                            key={`${option.code_type}-${option.code}-${option.description}`}
                            className="editRule-suggestion-item"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleSelectPathwayOption(option);
                            }}
                          >
                            {option.code}
                          </button>
                        ))}
                        {pathwayLoading && (
                          <div className="editRule-suggestions-empty editRule-suggestions-loading">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Loading more...
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Trigger Codes */}
          <section className="editRule-section">
            <h2 className="editRule-section-title">Trigger Codes</h2>

            <div className="editRule-field">
              <label className="editRule-label" htmlFor="trigger-codes">
                ICD-10 /Achi Codes
              </label>
              <div className="editRule-combobox">
                <div className="editRule-codes-input-row">
                  <input
                    id="trigger-codes"
                    type="text"
                    autoComplete="off"
                    className="editRule-input editRule-input--codes"
                    placeholder="Type to search codes..."
                    value={codeQuery}
                    disabled={isLoadingRule}
                    onChange={(e) => {
                      setCodeQuery(e.target.value);
                      setCodeOpen(true);
                    }}
                    onFocus={() => setCodeOpen(true)}
                    onBlur={() => setTimeout(() => setCodeOpen(false), 120)}
                  />
                </div>
                {codeOpen && (
                  <div
                    className="editRule-suggestions"
                    onScroll={handleTriggerCodeSuggestionsScroll}
                  >
                    {filteredCodeSuggestions.length === 0 && triggerCodeLoading ? (
                      <div className="editRule-suggestions-empty editRule-suggestions-loading">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Searching...
                      </div>
                    ) : filteredCodeSuggestions.length === 0 ? (
                      <div className="editRule-suggestions-empty">
                        No matching codes.
                      </div>
                    ) : (
                      <>
                        {filteredCodeSuggestions.map((option) => (
                          <button
                            type="button"
                            key={`${option.code_type}-${option.code}`}
                            className="editRule-suggestion-item"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              addCode(option);
                            }}
                          >
                            <strong>{option.code}</strong> — {option.description}
                          </button>
                        ))}
                        {triggerCodeLoading && (
                          <div className="editRule-suggestions-empty editRule-suggestions-loading">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Loading more...
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                {codes.length > 0 && (
                  <div className="editRule-code-chips editRule-code-chips--below">
                    {codes.map((c) => (
                      <Tooltip key={c.code} title={c.label || c.code} arrow placement="top">
                        <span className="editRule-code-chip">
                          {c.code}
                          <button
                            type="button"
                            className="editRule-code-chip-remove"
                            aria-label={`Remove ${c.code}`}
                            onClick={() => removeCode(c.code)}
                          >
                            &#215;
                          </button>
                        </span>
                      </Tooltip>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <p className="editRule-hint">
              Select one or more diagnosis and/or procedure codes that will
              trigger this Guidance and Decision Support Rule.
            </p>
          </section>
          <section className="editRule-section">
            <h2 className="editRule-section-title">Guidance &amp; Decision Support</h2>

            <div className="editRule-field">
              <label className="editRule-label" htmlFor="configured-guidance">
                Configured Guidance
              </label>
              <textarea
                id="configured-guidance"
                className="editRule-textarea"
                rows={2}
                value={configuredGuidance}
                disabled={isLoadingRule}
                onChange={(e) => setConfiguredGuidance(e.target.value)}
              />
            </div>

            <div className="editRule-field">
              <label className="editRule-label" htmlFor="recommended-action">
                Recommended Action
              </label>
              <textarea
                id="recommended-action"
                className="editRule-textarea"
                rows={2}
                value={recommendedAction}
                disabled={isLoadingRule}
                onChange={(e) => setRecommendedAction(e.target.value)}
              />
            </div>
          </section>

          {/* {submitError && (
            <p className="editRule-hint editRule-hint--error" role="alert">
              {submitError}
            </p>
          )} */}
        </div>

        {/* Footer actions */}
        <div className="editRule-footer">
          <button type="button" className="editRule-btn editRule-btn--ghost" onClick={handleCancelEdit}>
            Cancel
          </button>
          <div className="editRule-footer-right">
             <button
              type="button"
              className="editRule-btn editRule-btn--primary"
              disabled={!isSubmitEnabled || !!pendingAction}
              onClick={handleSubmit}
            >
              {pendingAction
                ? (pendingAction === RULE_MANAGEMENT_ACTIONS.SAVE ? "Saving..." : "Submitting...")
                : submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
    <PublishStatusModal
  show={showPublishModal}
  type={publishModalType}
  environment={[]}
  resultItems={ruleStatusResponse?.items || []}

  title="Rule Processing Information"

  loadingTitle={
    submitActionType === RULE_MANAGEMENT_ACTIONS.SAVE
      ? "Saving Your Rule"
      : "Submitting Your Rule for Testing"
  }

  successTitle={
    submitActionType === RULE_MANAGEMENT_ACTIONS.SAVE
      ? "Rule Saved Successfully"
      : "Rule Submitted for Testing"
  }

  loadingMessage={
    submitActionType === RULE_MANAGEMENT_ACTIONS.SAVE
      ? "Your rule is being saved."
      : "Your rule is being saved and submitted for testing."
  }

  successMessage={
    submitActionType === RULE_MANAGEMENT_ACTIONS.SAVE
      ? "Your rule has been successfully saved."
      : "Your rule has been successfully submitted for testing."
  }

  loadingInfoMessage="Please do not close or refresh this page until processing is complete."

  failedTitle="Rule Processing Failed"
  failedMessage="We couldn't complete the requested action."
  failedInfoMessage="Please review the details and try again."

  onClose={() => {
    setShowPublishModal(false);

    // Navigate only after the result modal is closed
    if (publishModalType === "result") {
      if (submitActionType === RULE_MANAGEMENT_ACTIONS.SAVE_AND_TEST) {
        navigate("/test-rule-table");
      } else {
        navigate(isArchived ? "/archived-rule" : "/view-rule-database");
      }
    }
  }}
/>
    </>

  );
}